'use client'

import { useDialogContext, useStoreState } from '@ariakit/react'
import { ComponentProps, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import { fromMd, timingSlower } from '@syconium/little-miss-figgy'
import { TypeStyleHeadlineMicro } from '@syconium/little-miss-figgy/dist/components/TypeStyle'

import { MiniCartContent } from '../../../components/pages/cart/MiniCart/MiniCartContent'
import {
  MiniCartDialogHeader,
  MiniCartDialogHeaderContent,
} from '../../../components/pages/cart/MiniCart/MiniCartDialogHeader'
import { miniCartWidthDesktop, miniCartWidthMobile } from '../../../constants'
import { useCart, useCartDialog } from '../../_providers/CartProvider.client'
import { NextDialogBackdrop, useNextDialogContext } from '../dialogs/NextDialog.client'
import { NextDrawerDialog } from '../dialogs/NextDrawerDialog.client'

import { PrimaryNavMenuButton } from './PrimaryNavMenus.client'

const PrimaryNavCartMenuBody = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;

  > * {
    white-space: nowrap;
  }
`

export const PrimaryNavCartDialogTrigger = ({ children }: { children?: React.ReactNode }) => {
  const { open } = useCartDialog()
  return (
    <PrimaryNavMenuButton onClick={open}>
      <PrimaryNavCartMenuBody>{children}</PrimaryNavCartMenuBody>
    </PrimaryNavMenuButton>
  )
}

const PrimaryNavCartHeader = styled(MiniCartDialogHeader)`
  padding-right: 60px;
  position: static;
  z-index: unset;
`

const CartDialogBackdrop = styled(NextDialogBackdrop)`
  background: ${o => o.theme.color.deprecated.gray.lightest};
  backdrop-filter: none;
  &[data-enter] {
    opacity: 0.9;
    backdrop-filter: none;
  }
`

const CartDialogContent = styled.div`
  position: relative;
  height: 100%;
`

type PrimaryNavCartDialogProps = {
  children?: React.ReactNode
  className?: String
} & Omit<ComponentProps<typeof NextDrawerDialog>, 'open'>

export const PrimaryNavCartDialog = styled(
  ({ onClose: onCloseProp, ...rest }: PrimaryNavCartDialogProps) => {
    const { isOpen: shouldBeOpen, close: setShouldBeOpenToFalse } = useCartDialog()
    const dialogContext = useDialogContext()
    const isOpen = dialogContext?.getState().open

    const onClose = useCallback<NonNullable<typeof onCloseProp>>(
      event => {
        onCloseProp?.(event)
        setShouldBeOpenToFalse()
      },
      [onCloseProp, setShouldBeOpenToFalse]
    )

    useEffect(() => {
      if (shouldBeOpen !== isOpen) {
        dialogContext?.setState('open', shouldBeOpen)
      }
    }, [dialogContext, isOpen, shouldBeOpen])

    return (
      <NextDrawerDialog
        {...rest}
        onClose={onClose}
        backdrop={<CartDialogBackdrop />}
        backdropTiming={timingSlower}
        content={<CartDialogContent />}
        role='dialog'
        aria-labelledby='minicartDialogTitle'
      />
    )
  }
)`
  background-color: ${o => o.theme.color.fill.subtle};
  width: ${miniCartWidthMobile};

  ${fromMd} {
    width: ${miniCartWidthDesktop};
  }
`

export const PrimaryNavCartDialogContent = () => {
  const dialogContext = useNextDialogContext()
  const dialogState = useStoreState(dialogContext)
  const isOpen = dialogState?.open

  if (!isOpen) return null
  return (
    <>
      <PrimaryNavCartHeader>
        <MiniCartDialogHeaderContent />
      </PrimaryNavCartHeader>
      <MiniCartContent isRevealed={true} />
    </>
  )
}

export const CartItemCountBadge = styled(({ className }: { className?: string }) => {
  const { cart } = useCart()
  if (cart.itemCount === 0) return null
  return (
    <span className={className}>
      <TypeStyleHeadlineMicro>
        {cart.itemCount > 99 ? '99+' : cart.itemCount}
      </TypeStyleHeadlineMicro>
    </span>
  )
})`
  box-sizing: border-box;
  min-width: ${o => o.theme.spacing(5)};
  min-height: ${o => o.theme.spacing(5)};
  padding: 0px ${o => o.theme.spacing(1)};
  position: absolute;
  right: 50%;
  top: ${o => o.theme.spacing(2.5)};
  border-radius: calc(${o => o.theme.spacing(5)} / 2);
  transform: translateX(min(100%, ${o => o.theme.spacing(6.5)}));

  background: ${o => o.theme.color.stroke.info.text.on.background};
  color: ${o =>
    o.theme.color.fill
      .background}; // If we use a text stroke for the background, the foreground needs to be a background color to ensure proper contrast ratios
`
